/**
 * Jargon-free grant dialog (polish task 04): "who → what program → what
 * level". The admin picks a program by name (typeahead), an access level
 * (role bundle shown by display name + plain description), and a reason. The
 * hook ensures the program's scope node exists and creates the assignment —
 * no scope_node ids, no bundle keys surfaced. Works for a user OR a group
 * principal (whichever id is passed).
 */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Timestamp } from '@bufbuild/protobuf';
import { Button, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, Input, Label, SpecraCombobox, Textarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { useListBundles } from './Access/hooks';
import { useGrantOnProgram, usePrograms } from './People/hooks';
import { actionLabel, ViewModeBadge } from './Permissions/PermissionsPage';
export function GrantDialog({ open, onOpenChange, userId, groupId, principalLabel }) {
    const [programQuery, setProgramQuery] = useState('');
    const [program, setProgram] = useState(null);
    const [bundleKey, setBundleKey] = useState('');
    const [reason, setReason] = useState('');
    // Blank by default — a blank window is a permanent grant, today's behavior.
    const [effectiveStart, setEffectiveStart] = useState('');
    const [effectiveEnd, setEffectiveEnd] = useState('');
    const { data: programs } = usePrograms(programQuery);
    const { data: bundles } = useListBundles(true);
    const grant = useGrantOnProgram();
    const reset = ()=>{
        setProgramQuery('');
        setProgram(null);
        setBundleKey('');
        setReason('');
        setEffectiveStart('');
        setEffectiveEnd('');
    };
    const canSubmit = !!program && !!bundleKey && reason.trim().length > 0 && !grant.isPending;
    const submit = async ()=>{
        if (!program) return;
        await grant.mutateAsync({
            userId,
            groupId,
            programId: program.id,
            programName: program.name,
            roleBundleKey: bundleKey,
            reason: reason.trim(),
            // Start of day / end of day, local — same convention as the designee
            // window on /admin/access (GrantAssignmentDialog).
            effectiveStart: effectiveStart ? Timestamp.fromDate(new Date(`${effectiveStart}T00:00:00`)) : undefined,
            effectiveEnd: effectiveEnd ? Timestamp.fromDate(new Date(`${effectiveEnd}T23:59:59`)) : undefined
        });
        reset();
        onOpenChange(false);
    };
    return /*#__PURE__*/ _jsx(Dialog, {
        open: open,
        onOpenChange: (o)=>{
            if (!o) reset();
            onOpenChange(o);
        },
        children: /*#__PURE__*/ _jsxs(DialogContent, {
            "data-testid": Testids.adminGrant.dialog,
            children: [
                /*#__PURE__*/ _jsxs(DialogHeader, {
                    children: [
                        /*#__PURE__*/ _jsxs(DialogTitle, {
                            children: [
                                "Grant access — ",
                                principalLabel
                            ]
                        }),
                        /*#__PURE__*/ _jsx(DialogDescription, {
                            children: "Choose a program and an access level. We handle the wiring."
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-4",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1.5",
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    children: "Program"
                                }),
                                program ? /*#__PURE__*/ _jsxs("div", {
                                    className: "flex items-center justify-between rounded-md border px-3 py-2 text-sm",
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            children: program.name
                                        }),
                                        /*#__PURE__*/ _jsx(Button, {
                                            variant: "ghost",
                                            size: "sm",
                                            onClick: ()=>setProgram(null),
                                            children: "Change"
                                        })
                                    ]
                                }) : /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsx(Input, {
                                            "data-testid": Testids.adminGrant.programSearch,
                                            placeholder: "Search programs by name or contract number",
                                            value: programQuery,
                                            onChange: (e)=>setProgramQuery(e.target.value)
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "max-h-40 overflow-auto rounded-md border",
                                            children: [
                                                (programs ?? []).map((p)=>/*#__PURE__*/ _jsxs("button", {
                                                        type: "button",
                                                        "data-testid": Testids.adminGrant.programOption,
                                                        "data-program-id": p.id,
                                                        className: "block w-full px-3 py-2 text-left text-sm hover:bg-muted",
                                                        onClick: ()=>setProgram({
                                                                id: p.id,
                                                                name: p.programNameFull || p.contractNumber || p.id
                                                            }),
                                                        children: [
                                                            /*#__PURE__*/ _jsx("div", {
                                                                children: p.programNameFull || '(unnamed program)'
                                                            }),
                                                            p.contractNumber ? /*#__PURE__*/ _jsx("div", {
                                                                className: "text-xs text-muted-foreground",
                                                                children: p.contractNumber
                                                            }) : null
                                                        ]
                                                    }, p.id)),
                                                (programs ?? []).length === 0 && /*#__PURE__*/ _jsx("div", {
                                                    className: "px-3 py-2 text-sm text-muted-foreground",
                                                    children: "No programs found"
                                                })
                                            ]
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1.5",
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    children: "Access level"
                                }),
                                /*#__PURE__*/ _jsx(SpecraCombobox, {
                                    label: "Access level",
                                    options: (bundles ?? []).map((b)=>({
                                            value: b.key,
                                            label: b.displayName
                                        })),
                                    value: bundleKey || undefined,
                                    onValueChange: (v)=>setBundleKey(v ?? ''),
                                    placeholder: "Choose an access level",
                                    searchPlaceholder: "Search access levels…",
                                    triggerProps: {
                                        'data-testid': Testids.adminGrant.bundleSelect
                                    }
                                }),
                                bundleKey && /*#__PURE__*/ _jsx(BundlePermissionsHint, {
                                    bundle: bundles?.find((b)=>b.key === bundleKey)
                                }),
                                bundleKey && /*#__PURE__*/ _jsx("p", {
                                    className: "text-xs text-muted-foreground",
                                    children: bundles?.find((b)=>b.key === bundleKey)?.description ?? ''
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "grid grid-cols-2 gap-3",
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-1.5",
                                    children: [
                                        /*#__PURE__*/ _jsx(Label, {
                                            children: "Starts on"
                                        }),
                                        /*#__PURE__*/ _jsx(Input, {
                                            type: "date",
                                            "data-testid": Testids.adminGrant.startDateInput,
                                            value: effectiveStart,
                                            onChange: (e)=>setEffectiveStart(e.target.value)
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-1.5",
                                    children: [
                                        /*#__PURE__*/ _jsx(Label, {
                                            children: "Ends on"
                                        }),
                                        /*#__PURE__*/ _jsx(Input, {
                                            type: "date",
                                            "data-testid": Testids.adminGrant.endDateInput,
                                            value: effectiveEnd,
                                            onChange: (e)=>setEffectiveEnd(e.target.value)
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx("p", {
                            className: "text-xs text-muted-foreground",
                            children: "Leave both blank for a permanent grant — the default."
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1.5",
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    children: "Reason"
                                }),
                                /*#__PURE__*/ _jsx(Textarea, {
                                    "data-testid": Testids.adminGrant.reasonInput,
                                    placeholder: "Why this access is being granted (appears in the audit log)",
                                    value: reason,
                                    onChange: (e)=>setReason(e.target.value)
                                })
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex justify-end gap-2",
                    children: [
                        /*#__PURE__*/ _jsx(Button, {
                            variant: "outline",
                            onClick: ()=>onOpenChange(false),
                            children: "Cancel"
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            "data-testid": Testids.adminGrant.submitButton,
                            disabled: !canSubmit,
                            onClick: submit,
                            children: "Grant access"
                        })
                    ]
                })
            ]
        })
    });
}
// The chosen access level, decoded: its actions and visibility, inline in the
// grant flow — so an admin picks by consequence, not by name.
function BundlePermissionsHint({ bundle }) {
    if (!bundle) return null;
    return /*#__PURE__*/ _jsx("div", {
        className: "rounded-md border bg-muted/20 px-2 py-1.5",
        children: /*#__PURE__*/ _jsxs("div", {
            className: "flex items-center gap-2",
            children: [
                /*#__PURE__*/ _jsx(ViewModeBadge, {
                    mode: bundle.defaultViewMode
                }),
                /*#__PURE__*/ _jsx("span", {
                    className: "flex flex-wrap gap-1",
                    children: bundle.permittedActions.map((a)=>/*#__PURE__*/ _jsx("span", {
                            className: "rounded bg-background px-1.5 py-0.5 text-[10px] ring-1 ring-border",
                            children: actionLabel(a)
                        }, a))
                })
            ]
        })
    });
}
